import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { History, Download, Filter, X, ArrowUpRight, ArrowDownRight, Minus, Undo2, Home, Layers, CheckSquare, Square, Image as ImageIcon } from "lucide-react";

// Session Z63 — Cleaning cost audit log viewer.
// Lists every row from cleaning_cost_history with filters by actor,
// property, action type and date range. Powers landlord dispute packs.

const ACTIONS = [
  { key: "", label: "All actions" },
  { key: "direct_edit", label: "Direct edit" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "proposed", label: "Proposed" },
];

const _fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch (_e) { return iso || ""; }
};

// Session Z68 — Serialise the current overlay SVG to a PNG blob and
// trigger a download. Pure client-side — no server round-trip, so the
// zoomed state the user is looking at is EXACTLY what lands in the file.
const exportOverlayPng = async (overlayData, overlayZoom) => {
  const svgEl = document.querySelector('[data-testid="audit-overlay-chart"]');
  if (!svgEl) return;
  try {
    const clone = svgEl.cloneNode(true);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("width", 720);
    clone.setAttribute("height", 320);
    // Give the exported PNG a white background and a light header so a
    // landlord dropping it into a dispute pack sees context, not just
    // a floating chart.
    const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    bg.setAttribute("x", "0"); bg.setAttribute("y", "0");
    bg.setAttribute("width", "720"); bg.setAttribute("height", "320");
    bg.setAttribute("fill", "white");
    clone.insertBefore(bg, clone.firstChild);
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(clone);
    const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.decoding = "sync";
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });
    const scale = 2;  // retina-sharp
    const canvas = document.createElement("canvas");
    canvas.width = 720 * scale;
    canvas.height = 360 * scale;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // Header text
    ctx.fillStyle = "#0a1128";
    ctx.font = "600 16px Georgia, serif";
    const names = overlayData.map((s) => s.name).join("  ·  ");
    ctx.fillText(`Cleaning cost overlay · ${names}`, 16 * scale, 24 * scale);
    ctx.font = "12px Helvetica, Arial";
    ctx.fillStyle = "#64748b";
    const subtitle = overlayZoom
      ? `Zoomed: ${new Date(overlayZoom.minT).toLocaleDateString("en-GB")} → ${new Date(overlayZoom.maxT).toLocaleDateString("en-GB")}`
      : "Full history";
    ctx.fillText(subtitle, 16 * scale, 42 * scale);
    // Draw the chart below the header.
    ctx.drawImage(img, 0, 48 * scale, 720 * scale, 320 * scale);
    URL.revokeObjectURL(url);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `cleaning-cost-overlay_${today}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(dlUrl), 500);
    }, "image/png");
  } catch (e) {
    // Soft-fail with a toast — PNG export is nice-to-have.
    import("sonner").then(({ toast }) => toast.error("PNG export failed. Try reopening the overlay."));
  }
};

const _actionBadge = (action) => {
  const styles = {
    direct_edit: "bg-slate-100 text-slate-700",
    approved: "bg-teal-50 text-teal-700",
    rejected: "bg-red-50 text-red-700",
    proposed: "bg-amber-50 text-amber-700",
  };
  const cls = styles[action] || "bg-slate-100 text-slate-700";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${cls}`}>
      {(action || "unknown").replace("_", " ")}
    </span>
  );
};

export default function CleaningCostAudit() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [rows, setRows] = useState([]);
  const [actors, setActors] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [drillRow, setDrillRow] = useState(null);
  const [drillHistory, setDrillHistory] = useState(null);
  const [drillBusy, setDrillBusy] = useState(false);
  // Session Z65 — overlay comparison: multi-select up to 3 properties.
  const [overlayPids, setOverlayPids] = useState([]);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayData, setOverlayData] = useState([]);
  const [overlayBusy, setOverlayBusy] = useState(false);
  // Session Z67 — drag-to-zoom state.
  const [overlayDrag, setOverlayDrag] = useState(null);
  const [overlayZoom, setOverlayZoom] = useState(null);
  const [filters, setFilters] = useState({
    actor: "",
    property_id: "",
    action: "",
    from_date: "",
    to_date: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.actor) params.set("actor", filters.actor);
      if (filters.property_id) params.set("property_id", filters.property_id);
      if (filters.action) params.set("action", filters.action);
      if (filters.from_date) params.set("from_date", filters.from_date);
      if (filters.to_date) params.set("to_date", filters.to_date);
      params.set("limit", "500");
      const { data } = await api.get(`/cleaning-cost-history/audit?${params.toString()}`);
      setRows(data?.items || []);
      setActors(data?.actors || []);
    } catch (err) {
      toast.error(apiError(err, "Load failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/cleaning-companies/rates").then((r) => {
      const items = r.data?.items || [];
      setProperties(items.map((x) => ({ id: x.property_id, name: x.property_name })));
    }).catch(() => setProperties([]));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const csvHref = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.actor) params.set("actor", filters.actor);
    if (filters.property_id) params.set("property_id", filters.property_id);
    if (filters.action) params.set("action", filters.action);
    if (filters.from_date) params.set("from_date", filters.from_date);
    if (filters.to_date) params.set("to_date", filters.to_date);
    return `/api/cleaning-cost-history/audit.csv?${params.toString()}`;
  }, [filters]);

  const clearFilters = () => {
    setFilters({ actor: "", property_id: "", action: "", from_date: "", to_date: "" });
    setTimeout(load, 0);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const revertRow = async (r) => {
    if (!window.confirm(
      `Revert ${r.property_name || "this property"} back to £${Number(r.from_value || 0).toFixed(2)}?\n\n`
      + `Current committed cost: £${Number(r.to_value || 0).toFixed(2)}.`
    )) return;
    setReverting(r.id);
    try {
      const { data } = await api.post(`/cleaning-cost-history/${r.id}/revert`);
      if (data.noop) {
        toast.info(data.message || "Property already matches the target value.");
      } else {
        toast.success(`Reverted ${data.property_name} to £${Number(data.to).toFixed(2)}`);
      }
      load();
    } catch (err) {
      toast.error(apiError(err, "Revert failed"));
    } finally {
      setReverting(null);
    }
  };

  const openDrill = async (r) => {
    if (!r.property_id) return;
    setDrillRow(r);
    setDrillHistory(null);
    setDrillBusy(true);
    try {
      const { data } = await api.get(
        `/cleaning-cost-history/audit?property_id=${r.property_id}&limit=200`,
      );
      // Order ASC for a left-to-right timeline.
      const items = (data?.items || []).slice().reverse();
      setDrillHistory(items);
    } catch (err) {
      toast.error(apiError(err, "Couldn't load drill-down"));
      setDrillRow(null);
    } finally {
      setDrillBusy(false);
    }
  };

  // Session Z65 — overlay compare multi-select handlers.
  const toggleOverlay = (pid) => {
    setOverlayPids((prev) => {
      if (prev.includes(pid)) return prev.filter((x) => x !== pid);
      if (prev.length >= 3) {
        toast.info("Overlay mode compares up to 3 properties at once.");
        return prev;
      }
      return [...prev, pid];
    });
  };

  const openOverlay = async () => {
    if (overlayPids.length < 2) {
      toast.info("Tick at least 2 property rows to compare.");
      return;
    }
    setOverlayBusy(true);
    try {
      const results = await Promise.all(overlayPids.map((pid) =>
        api.get(`/cleaning-cost-history/audit?property_id=${pid}&limit=200`)
          .then((r) => ({
            pid,
            name: properties.find((p) => p.id === pid)?.name || "(unknown)",
            // ASC so left-to-right reads chronologically.
            points: (r.data?.items || []).slice().reverse(),
          }))
      ));
      setOverlayData(results);
      setOverlayOpen(true);
    } catch (err) {
      toast.error(apiError(err, "Couldn't build overlay"));
    } finally {
      setOverlayBusy(false);
    }
  };

  const bulkRevert = async () => {
    // Session Z66 — always run a dry-run first and surface the preview
    // modal so the owner sees every from/to before confirming.
    setBulkBusy(true);
    try {
      const payload = {
        dry_run: true,
        actor: filters.actor || null,
        property_id: filters.property_id || null,
        action: filters.action || null,
        from_date: filters.from_date || null,
        to_date: filters.to_date || null,
      };
      const { data } = await api.post("/cleaning-cost-history/bulk-revert", payload);
      if ((data.details || []).length === 0) {
        toast.info("Nothing to revert in the current filter.");
        return;
      }
      setBulkPreview({ ...data, filters: payload });
    } catch (err) {
      toast.error(apiError(err, "Dry-run failed"));
    } finally {
      setBulkBusy(false);
    }
  };

  const confirmBulkRevert = async () => {
    if (!bulkPreview) return;
    setBulkBusy(true);
    try {
      const payload = { ...bulkPreview.filters, dry_run: false, confirm: true };
      const { data } = await api.post("/cleaning-cost-history/bulk-revert", payload);
      // Session Z67 — toast includes an undo button powered by the bulk_op_id.
      if (data.bulk_op_id) {
        toast.success(
          `Reverted ${data.reverted} propert${data.reverted === 1 ? "y" : "ies"} · ${data.skipped} skipped`,
          {
            action: {
              label: "Undo",
              onClick: () => rollbackBulkOp(data.bulk_op_id),
            },
            duration: 15000,
          },
        );
      } else {
        toast.success(`Reverted ${data.reverted} propert${data.reverted === 1 ? "y" : "ies"} · ${data.skipped} skipped`);
      }
      setBulkPreview(null);
      load();
    } catch (err) {
      toast.error(apiError(err, "Bulk revert failed"));
    } finally {
      setBulkBusy(false);
    }
  };

  const rollbackBulkOp = async (bulkOpId) => {
    if (!window.confirm("Roll this bulk revert back? Every property it touched will return to its post-revert value is undone.")) return;
    try {
      const { data } = await api.post(`/cleaning-cost-history/bulk-rollback/${bulkOpId}`);
      toast.success(`Rolled back ${data.rolled_back} propert${data.rolled_back === 1 ? "y" : "ies"}`);
      load();
    } catch (err) {
      toast.error(apiError(err, "Rollback failed"));
    }
  };

  return (
    <Layout
      title="Cleaning cost audit"
      subtitle="Every proposed, approved and directly-edited cleaning cost across the portfolio. Filter by anyone or anything to build a dispute pack in seconds."
    >
      <section className="ns-card p-6" data-testid="cleaning-cost-audit">
        <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
          <div className="min-w-0">
            <div className="ns-label">Audit log</div>
            <div className="font-serif text-xl mt-1">Every cost change on record</div>
            <div className="text-xs text-slate-400 mt-1">
              Backed by <code>cleaning_cost_history</code>. Rolling capture, no retention cap.
            </div>
          </div>
          <div className="w-9 h-9 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center shrink-0">
            <History className="w-4 h-4" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          <div>
            <Label className="ns-label mb-1 block">Actor</Label>
            <Select
              value={filters.actor || "__all__"}
              onValueChange={(v) => setFilters((f) => ({ ...f, actor: v === "__all__" ? "" : v }))}
            >
              <SelectTrigger data-testid="audit-filter-actor" className="h-10 bg-white">
                <SelectValue placeholder="Anyone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Anyone</SelectItem>
                {actors.map((a) => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="ns-label mb-1 block">Property</Label>
            <Select
              value={filters.property_id || "__all__"}
              onValueChange={(v) => setFilters((f) => ({ ...f, property_id: v === "__all__" ? "" : v }))}
            >
              <SelectTrigger data-testid="audit-filter-property" className="h-10 bg-white">
                <SelectValue placeholder="Any property" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Any property</SelectItem>
                {properties.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="ns-label mb-1 block">Action</Label>
            <Select
              value={filters.action || "__all__"}
              onValueChange={(v) => setFilters((f) => ({ ...f, action: v === "__all__" ? "" : v }))}
            >
              <SelectTrigger data-testid="audit-filter-action" className="h-10 bg-white">
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent>
                {ACTIONS.map((a) => (
                  <SelectItem key={a.key || "__all__"} value={a.key || "__all__"}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="ns-label mb-1 block">From</Label>
            <Input
              type="date"
              data-testid="audit-filter-from"
              value={filters.from_date}
              onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
              className="h-10 bg-white"
            />
          </div>
          <div>
            <Label className="ns-label mb-1 block">To</Label>
            <Input
              type="date"
              data-testid="audit-filter-to"
              value={filters.to_date}
              onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
              className="h-10 bg-white"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Button
            type="button"
            data-testid="audit-apply"
            onClick={load}
            disabled={loading}
            className="rounded-full ns-btn-primary h-10 px-5 gap-2"
          >
            <Filter className="w-4 h-4" />
            {loading ? "Filtering…" : "Apply filters"}
          </Button>
          {activeFilterCount > 0 && (
            <Button
              type="button"
              variant="outline"
              data-testid="audit-clear"
              onClick={clearFilters}
              className="rounded-full h-10 px-4 gap-2"
            >
              <X className="w-4 h-4" />
              Clear {activeFilterCount}
            </Button>
          )}
          <a
            href={csvHref}
            target="_blank"
            rel="noreferrer"
            data-testid="audit-csv"
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-teal-600 hover:text-teal-700 text-[12px] h-10 px-4"
            title="Download the current filtered view as CSV"
          >
            <Download className="w-4 h-4" />
            Download CSV
          </a>
          {isOwner && activeFilterCount > 0 && rows.length > 0 && (
            <Button
              type="button"
              variant="outline"
              data-testid="audit-bulk-revert"
              onClick={bulkRevert}
              disabled={bulkBusy}
              className="rounded-full h-10 px-4 gap-2 border-red-200 text-red-700 hover:bg-red-50"
              title="Revert every property affected by the current filter"
            >
              <Undo2 className="w-4 h-4" />
              {bulkBusy ? "Reverting…" : `Bulk revert (${rows.length})`}
            </Button>
          )}
          {overlayPids.length >= 2 && (
            <Button
              type="button"
              data-testid="audit-overlay-open"
              onClick={openOverlay}
              disabled={overlayBusy}
              className="rounded-full h-10 px-4 gap-2 bg-slate-900 hover:bg-slate-700 text-white"
            >
              <Layers className="w-4 h-4" />
              {overlayBusy ? "Loading…" : `Compare ${overlayPids.length}`}
            </Button>
          )}
          <div className="ml-auto text-xs text-slate-400" data-testid="audit-count">
            {rows.length} row{rows.length === 1 ? "" : "s"}
            {overlayPids.length > 0 && <span className="ml-2 text-teal-700">· {overlayPids.length} selected to compare</span>}
          </div>
        </div>

        <div className="overflow-x-auto -mx-6">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-slate-500">
              <tr>
                <th className="ns-label px-6 py-3 w-8">&nbsp;</th>
                <th className="ns-label px-6 py-3 text-left">When</th>
                <th className="ns-label px-6 py-3 text-left">Property</th>
                <th className="ns-label px-6 py-3 text-left">Action</th>
                <th className="ns-label px-6 py-3 text-right">From</th>
                <th className="ns-label px-6 py-3 text-right">To</th>
                <th className="ns-label px-6 py-3 text-right">Δ</th>
                <th className="ns-label px-6 py-3 text-left">Actor</th>
                <th className="ns-label px-6 py-3 text-right">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="text-center py-10 text-slate-400 italic">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} data-testid="audit-empty" className="text-center py-10 text-slate-400 italic">
                    No changes match this filter — try widening the date range or clearing filters.
                  </td>
                </tr>
              )}
              {rows.map((r, i) => {
                const delta = r.delta;
                const DeltaIcon = delta == null ? Minus : delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Minus;
                const deltaColour = delta == null ? "text-slate-400" : delta > 0 ? "text-red-700" : delta < 0 ? "text-teal-700" : "text-slate-400";
                const canRevert = isOwner
                  && r.action !== "revert"
                  && r.action !== "bulk_revert"
                  && r.from_value != null
                  && r.property_id;
                const inOverlay = r.property_id && overlayPids.includes(r.property_id);
                return (
                  <tr key={r.id || i} data-testid={`audit-row-${i}`} className={`border-t border-slate-100 hover:bg-stone-50/40 ${inOverlay ? "bg-teal-50/40" : ""}`}>
                    <td className="px-3 py-3 text-center">
                      {r.property_id && (
                        <button
                          type="button"
                          onClick={() => toggleOverlay(r.property_id)}
                          data-testid={`audit-overlay-pick-${i}`}
                          className="text-slate-400 hover:text-teal-700"
                          title={inOverlay ? "Remove from overlay" : "Add to overlay comparison (up to 3)"}
                        >
                          {inOverlay ? <CheckSquare className="w-4 h-4 text-teal-700" /> : <Square className="w-4 h-4" />}
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-3 text-slate-600 text-[13px] whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => openDrill(r)}
                        data-testid={`audit-drill-${i}`}
                        className="hover:text-teal-700 hover:underline text-left"
                        title="Open per-property timeline"
                      >
                        {_fmtDate(r.at)}
                      </button>
                    </td>
                    <td className="px-6 py-3 font-medium text-slate-800 min-w-[220px]">
                      <button
                        type="button"
                        onClick={() => openDrill(r)}
                        className="hover:text-teal-700 hover:underline text-left"
                      >
                        {r.property_name || <span className="italic text-slate-400">(deleted property)</span>}
                      </button>
                    </td>
                    <td className="px-6 py-3">{_actionBadge(r.action)}</td>
                    <td className="px-6 py-3 text-right text-slate-600 tabular-nums">
                      {r.from_value == null ? <span className="text-slate-300">—</span> : `£${Number(r.from_value).toFixed(2)}`}
                    </td>
                    <td className="px-6 py-3 text-right font-semibold text-slate-900 tabular-nums">
                      £{Number(r.to_value || 0).toFixed(2)}
                    </td>
                    <td className={`px-6 py-3 text-right tabular-nums ${deltaColour}`}>
                      <span className="inline-flex items-center gap-0.5">
                        <DeltaIcon className="w-3 h-3" />
                        {delta == null ? "—" : `${delta >= 0 ? "+" : ""}£${Math.abs(delta).toFixed(2)}`}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-slate-600">
                      {r.actor_name || <span className="italic text-slate-400">—</span>}
                      {r.proposer_name && r.proposer_name !== r.actor_name && (
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          proposed by {r.proposer_name}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right">
                      {canRevert && (
                        <button
                          type="button"
                          data-testid={`audit-revert-${i}`}
                          onClick={() => revertRow(r)}
                          disabled={reverting === r.id}
                          className="inline-flex items-center gap-1 text-[11px] rounded-full border border-slate-200 bg-white text-slate-600 hover:border-teal-600 hover:text-teal-700 px-2.5 py-1 disabled:opacity-40"
                          title={`Revert to £${Number(r.from_value || 0).toFixed(2)}`}
                        >
                          <Undo2 className="w-3 h-3" />
                          Revert
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {drillRow && (
        <div
          data-testid="audit-drill-dialog"
          onClick={() => setDrillRow(null)}
          className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-xl max-w-3xl w-full p-6 relative"
          >
            <button
              type="button"
              onClick={() => setDrillRow(null)}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="ns-label text-teal-700 flex items-center gap-2">
              <Home className="w-3 h-3" /> Property timeline
            </div>
            <div className="font-serif text-2xl mt-1 text-slate-900">
              {drillRow.property_name || "(deleted property)"}
            </div>
            {drillBusy && (
              <div className="text-sm text-slate-400 italic py-10 text-center">Loading timeline…</div>
            )}
            {!drillBusy && drillHistory && drillHistory.length === 0 && (
              <div className="text-sm text-slate-400 italic py-10 text-center">
                No prior changes recorded for this property.
              </div>
            )}
            {!drillBusy && drillHistory && drillHistory.length > 0 && (() => {
              const pts = drillHistory;
              const vals = pts.map((p) => Number(p.to_value) || 0);
              const w = 620, h = 240;
              const pad = { t: 24, r: 32, b: 42, l: 56 };
              const innerW = w - pad.l - pad.r;
              const innerH = h - pad.t - pad.b;
              const minV = Math.min(...vals);
              const maxV = Math.max(...vals);
              const padY = Math.max((maxV - minV) * 0.15, 1);
              const yMin = Math.max(0, minV - padY);
              const yMax = maxV + padY;
              const yRange = yMax - yMin || 1;
              const stepX = vals.length > 1 ? innerW / (vals.length - 1) : 0;
              const coords = vals.map((v, i) => {
                const x = pad.l + i * stepX;
                const y = pad.t + innerH - ((v - yMin) / yRange) * innerH;
                return [x, y];
              });
              const path = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
              const area = coords.length > 1
                ? `${path} L ${coords[coords.length - 1][0]} ${pad.t + innerH} L ${coords[0][0]} ${pad.t + innerH} Z`
                : "";
              const stroke = "#0d7a75";
              const highlightIdx = pts.findIndex((p) => p.id === drillRow.id);
              const gridLines = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
                val: yMin + t * yRange,
                y: pad.t + innerH - t * innerH,
              }));
              return (
                <>
                  <div className="text-xs text-slate-400 mt-1 mb-3" data-testid="audit-drill-count">
                    {pts.length} change point{pts.length === 1 ? "" : "s"} on record
                  </div>
                  <svg width="100%" viewBox={`0 0 ${w} ${h}`} data-testid="audit-drill-chart">
                    {gridLines.map(({ val, y }, i) => (
                      <g key={i}>
                        <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke="#f1f5f9" strokeWidth="1" />
                        <text x={pad.l - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#94a3b8" fontFamily="monospace">
                          £{val.toFixed(0)}
                        </text>
                      </g>
                    ))}
                    {area && <path d={area} fill="#ccfbf1" opacity="0.5" style={{ pointerEvents: "none" }} />}
                    <path d={path} fill="none" stroke={stroke} strokeWidth="2" style={{ pointerEvents: "none" }} />
                    {coords.map(([x, y], i) => {
                      const p = pts[i];
                      const isHighlight = i === highlightIdx;
                      const curr = Number(p.to_value || 0);
                      const dateLabel = (() => {
                        try {
                          return new Date(p.at).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
                        } catch (_e) { return ""; }
                      })();
                      return (
                        <g key={i} data-testid={`audit-drill-point-${i}`}>
                          <circle
                            cx={x} cy={y}
                            r={isHighlight ? 6 : 4}
                            fill={isHighlight ? "#dc2626" : "white"}
                            stroke={isHighlight ? "#dc2626" : stroke}
                            strokeWidth="2"
                          />
                          <text x={x} y={y - 10} textAnchor="middle" fontSize="11" fontWeight="600" fill="#0a1128" fontFamily="Georgia, serif">
                            £{curr.toFixed(0)}
                          </text>
                          <text x={x} y={pad.t + innerH + 16} textAnchor="middle" fontSize="10" fill="#64748b">
                            {dateLabel}
                          </text>
                          <text x={x} y={pad.t + innerH + 30} textAnchor="middle" fontSize="9" fill="#94a3b8">
                            {p.actor_name || ""}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                  <div className="mt-4 text-[11px] text-slate-400 italic">
                    Red dot highlights the audit row you clicked. Full history, oldest left → newest right.
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
      {overlayOpen && (
        <div
          data-testid="audit-overlay-dialog"
          onClick={() => setOverlayOpen(false)}
          className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-xl max-w-4xl w-full p-6 relative"
          >
            <button
              type="button"
              onClick={() => setOverlayOpen(false)}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="ns-label text-teal-700 flex items-center gap-2">
              <Layers className="w-3 h-3" /> Overlay comparison
            </div>
            <div className="font-serif text-2xl mt-1 text-slate-900">
              {overlayData.length} propert{overlayData.length === 1 ? "y" : "ies"} side-by-side
            </div>
            <div className="text-xs text-slate-400 mt-1 mb-4">
              Real calendar axis — simultaneous spikes line up vertically so cross-portfolio drift becomes obvious.
            </div>
            {(() => {
              // Session Z66 — real-date x-axis. Each change point sits at
              // its actual timestamp, not its index. Lets owners spot
              // simultaneous spikes across properties.
              // Session Z67 — drag-to-zoom. Mouse-down + drag inside the
              // chart area defines a date range; releasing re-renders with
              // the narrowed window. Double-click resets.
              const palette = ["#0d7a75", "#dc2626", "#b45309"];
              const w = 720, h = 320;
              const pad = { t: 24, r: 32, b: 70, l: 56 };
              const innerW = w - pad.l - pad.r;
              const innerH = h - pad.t - pad.b;
              const seriesData = overlayData.map((s) => (s.points || []).map((p) => ({
                t: new Date(p.at).getTime(),
                v: Number(p.to_value) || 0,
              })).filter((p) => Number.isFinite(p.t)));
              const flatPts = seriesData.flat();
              if (flatPts.length === 0) {
                return <div className="text-sm text-slate-400 italic py-10 text-center">No history yet for these properties.</div>;
              }
              const dataMinT = Math.min(...flatPts.map((p) => p.t));
              const dataMaxT = Math.max(...flatPts.map((p) => p.t));
              const minT = overlayZoom?.minT ?? dataMinT;
              const maxT = overlayZoom?.maxT ?? dataMaxT;
              const tRange = Math.max(maxT - minT, 1);
              const visiblePts = flatPts.filter((p) => p.t >= minT && p.t <= maxT);
              const flatVals = (visiblePts.length ? visiblePts : flatPts).map((p) => p.v);
              const minV = Math.min(...flatVals);
              const maxV = Math.max(...flatVals);
              const padY = Math.max((maxV - minV) * 0.15, 1);
              const yMin = Math.max(0, minV - padY);
              const yMax = maxV + padY;
              const yRange = yMax - yMin || 1;
              const gridLines = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
                val: yMin + t * yRange,
                y: pad.t + innerH - t * innerH,
              }));
              const dateTicks = [0, 0.33, 0.66, 1].map((t) => {
                const ts = minT + t * tRange;
                return {
                  ts,
                  x: pad.l + t * innerW,
                  label: new Date(ts).toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
                };
              });
              const xOf = (t) => pad.l + ((t - minT) / tRange) * innerW;
              const yOf = (v) => pad.t + innerH - ((v - yMin) / yRange) * innerH;
              const xToTime = (x) => minT + ((x - pad.l) / innerW) * tRange;
              const onMouseDown = (e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const vbRatio = w / rect.width;
                const x = (e.clientX - rect.left) * vbRatio;
                if (x < pad.l || x > w - pad.r) return;
                setOverlayDrag({ startX: x, endX: x });
              };
              const onMouseMove = (e) => {
                if (!overlayDrag) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const vbRatio = w / rect.width;
                const x = Math.max(pad.l, Math.min(w - pad.r, (e.clientX - rect.left) * vbRatio));
                setOverlayDrag((d) => ({ ...d, endX: x }));
              };
              const onMouseUp = () => {
                if (!overlayDrag) return;
                const { startX, endX } = overlayDrag;
                setOverlayDrag(null);
                if (Math.abs(endX - startX) < 8) return;  // ignore accidental clicks
                const lo = Math.min(startX, endX);
                const hi = Math.max(startX, endX);
                setOverlayZoom({ minT: xToTime(lo), maxT: xToTime(hi) });
              };
              const onDoubleClick = () => setOverlayZoom(null);
              const dragRect = overlayDrag ? {
                x: Math.min(overlayDrag.startX, overlayDrag.endX),
                w: Math.abs(overlayDrag.endX - overlayDrag.startX),
              } : null;
              return (
                <>
                  <svg
                    width="100%"
                    viewBox={`0 0 ${w} ${h}`}
                    data-testid="audit-overlay-chart"
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={() => setOverlayDrag(null)}
                    onDoubleClick={onDoubleClick}
                    style={{ cursor: overlayDrag ? "ew-resize" : "crosshair", userSelect: "none" }}
                  >
                    {gridLines.map(({ val, y }, i) => (
                      <g key={i}>
                        <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke="#f1f5f9" strokeWidth="1" />
                        <text x={pad.l - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#94a3b8" fontFamily="monospace">
                          £{val.toFixed(0)}
                        </text>
                      </g>
                    ))}
                    {dateTicks.map((d, i) => (
                      <g key={`t-${i}`}>
                        <line x1={d.x} x2={d.x} y1={pad.t} y2={pad.t + innerH} stroke="#f8fafc" strokeWidth="1" />
                        <text x={d.x} y={pad.t + innerH + 14} textAnchor="middle" fontSize="10" fill="#94a3b8">
                          {d.label}
                        </text>
                      </g>
                    ))}
                    {seriesData.map((pts, si) => {
                      const colour = palette[si % palette.length];
                      const sorted = pts.slice().sort((a, b) => a.t - b.t);
                      const coords = sorted
                        .filter((p) => p.t >= minT && p.t <= maxT)
                        .map((p) => [xOf(p.t), yOf(p.v)]);
                      const path = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
                      return (
                        <g key={si}>
                          <path d={path} fill="none" stroke={colour} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                          {coords.map(([x, y], i) => (
                            <circle key={i} cx={x} cy={y} r="3" fill={colour} />
                          ))}
                        </g>
                      );
                    })}
                    {dragRect && (
                      <rect
                        x={dragRect.x}
                        y={pad.t}
                        width={dragRect.w}
                        height={innerH}
                        fill="#0d7a75"
                        fillOpacity="0.1"
                        stroke="#0d7a75"
                        strokeWidth="1"
                        strokeDasharray="3 3"
                      />
                    )}
                    {overlayData.map((s, si) => (
                      <g key={si} transform={`translate(${pad.l + si * 220}, ${h - 24})`}>
                        <circle cx="6" cy="6" r="4" fill={palette[si % palette.length]} />
                        <text x="18" y="10" fontSize="11" fill="#1e293b" fontFamily="Helvetica, Arial" data-testid={`audit-overlay-legend-${si}`}>
                          {s.name} ({s.points.length} pt{s.points.length === 1 ? "" : "s"})
                        </text>
                      </g>
                    ))}
                  </svg>
                  <div className="mt-2 text-[11px] text-slate-400 italic flex items-center justify-between gap-2">
                    <span>Drag horizontally to zoom a date range · double-click to reset.</span>
                    {overlayZoom && (
                      <button
                        type="button"
                        data-testid="audit-overlay-reset-zoom"
                        onClick={() => setOverlayZoom(null)}
                        className="text-teal-700 hover:underline"
                      >
                        Reset zoom
                      </button>
                    )}
                  </div>
                </>
              );
            })()}
            <div className="mt-4 flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setOverlayPids([]); setOverlayOpen(false); }}
                data-testid="audit-overlay-clear"
                className="rounded-full h-9 px-4 gap-2"
              >
                <X className="w-4 h-4" />
                Clear selection
              </Button>
              <Button
                type="button"
                data-testid="audit-overlay-png"
                onClick={() => exportOverlayPng(overlayData, overlayZoom)}
                className="rounded-full h-9 px-4 gap-2 ns-btn-primary ml-auto"
              >
                <ImageIcon className="w-4 h-4" />
                Export PNG
              </Button>
            </div>
          </div>
        </div>
      )}

      {bulkPreview && (
        <div
          data-testid="audit-bulk-preview-dialog"
          onClick={() => setBulkPreview(null)}
          className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 relative"
          >
            <button
              type="button"
              onClick={() => setBulkPreview(null)}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="ns-label text-red-700 flex items-center gap-2">
              <Undo2 className="w-3 h-3" /> Bulk revert preview
            </div>
            <div className="font-serif text-2xl mt-1 text-slate-900">
              {bulkPreview.reverted} propert{bulkPreview.reverted === 1 ? "y" : "ies"} will change
            </div>
            <div className="text-xs text-slate-400 mt-1 mb-4">
              Review every from → to before confirming. {bulkPreview.skipped > 0 && `${bulkPreview.skipped} will be skipped (already at target or missing).`}
            </div>
            <div className="max-h-80 overflow-y-auto border border-slate-100 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-slate-500 sticky top-0">
                  <tr>
                    <th className="ns-label px-4 py-2 text-left">Property</th>
                    <th className="ns-label px-4 py-2 text-right">From</th>
                    <th className="ns-label px-4 py-2 text-right">To</th>
                    <th className="ns-label px-4 py-2 text-right">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkPreview.details.map((d, i) => {
                    const delta = (d.from != null && d.to != null) ? d.to - d.from : null;
                    const colour = delta == null ? "text-slate-400" : delta > 0 ? "text-red-700" : "text-teal-700";
                    const skipped = !!d.skipped;
                    return (
                      <tr key={i} data-testid={`audit-bulk-preview-row-${i}`} className={`border-t border-slate-100 ${skipped ? "opacity-50" : ""}`}>
                        <td className="px-4 py-2 text-slate-800 font-medium">
                          {d.property_name || <span className="italic text-slate-400">(missing)</span>}
                          {skipped && <div className="text-[10px] uppercase tracking-wider text-amber-600 mt-0.5">{d.skipped.replace("_", " ")}</div>}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                          {d.from != null ? `£${Number(d.from).toFixed(2)}` : "—"}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums font-semibold text-slate-900">
                          {d.to != null ? `£${Number(d.to).toFixed(2)}` : "—"}
                        </td>
                        <td className={`px-4 py-2 text-right tabular-nums ${colour}`}>
                          {delta == null ? "—" : `${delta >= 0 ? "+" : ""}£${Math.abs(delta).toFixed(2)}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setBulkPreview(null)}
                data-testid="audit-bulk-preview-cancel"
                className="rounded-full h-10 px-5"
              >
                Cancel
              </Button>
              <Button
                type="button"
                data-testid="audit-bulk-preview-confirm"
                onClick={confirmBulkRevert}
                disabled={bulkBusy || bulkPreview.reverted === 0}
                className="rounded-full h-10 px-5 gap-2 bg-red-600 hover:bg-red-700 text-white"
              >
                <Undo2 className="w-4 h-4" />
                {bulkBusy ? "Reverting…" : `Confirm revert · ${bulkPreview.reverted}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
